import { useState } from 'react';
import { Button } from '@tigergraph/app-ui-lib/button';
import { Modal, ModalBody, ModalButton, ModalFooter, ModalHeader } from '@tigergraph/app-ui-lib/modal';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Checkbox } from '@tigergraph/app-ui-lib/checkbox';
import { Label } from '@tigergraph/app-ui-lib/typography';
import { useAtom } from 'jotai';
import toast from 'react-hot-toast';

import { calculateRoleForSpace } from '@/pages/admin/user/type';
import { useOrgContext } from '@/contexts/orgContext';
import { useMutationDeleteWorkspace, useMutationPauseWorkspace } from '@/pages/workgroup/hook';
import { deletingSpaceIdsAtom, workSpacesAtom } from '@/pages/workgroup/atom';
import SpaceUserManagement from '@/pages/workgroup/tab/iam/SpaceUserManagement';
import { WorkGroupT, WorkspaceT, isStatusActive, isStatusError, isStatusPending } from '@/pages/workgroup/type';
import { getErrorMessage } from '@/utils/utils';
import { expand } from 'inline-style-expand-shorthand';

export type SelectionBarProps = {
  group: WorkGroupT;
  selectedWorkspaces: WorkspaceT[];
  onClearSelection: () => void;
};

export default function SelectionBar({ group, selectedWorkspaces, onClearSelection }: SelectionBarProps) {
  const [css, theme] = useStyletron();
  const { userInfo } = useOrgContext();

  const [, setWorkspacesPolling] = useAtom(workSpacesAtom);
  const [, setDeletingSpaceIds] = useAtom(deletingSpaceIdsAtom);

  const pauseWorkSpaceMutation = useMutationPauseWorkspace();
  const deleteWorkSpaceMutation = useMutationDeleteWorkspace();

  const [showTerminateConfirm, setShowTerminateConfirm] = useState(false);
  const [terminateAcknowledged, setTerminateAcknowledged] = useState(false);
  const [showAccessForWorkspace, setShowAccessForWorkspace] = useState<WorkspaceT | null>(null);

  if (selectedWorkspaces.length === 0) {
    return null;
  }

  const adminWorkspaces = selectedWorkspaces.filter(
    (workspace) =>
      calculateRoleForSpace(userInfo.roles, workspace.workgroup_id, workspace.workspace_id) === 'workspace-admins'
  );

  const pausableWorkspaces = adminWorkspaces.filter(
    (workspace) =>
      isStatusActive(workspace.status) || isStatusError(workspace.status) || isStatusPending(workspace.status)
  );

  const terminableWorkspaces = adminWorkspaces.filter((workspace) => workspace.status !== 'Terminating');

  const canPause = pausableWorkspaces.length > 0;
  const canTerminate = terminableWorkspaces.length > 0;
  const canManageAccess = selectedWorkspaces.length === 1 && adminWorkspaces.length === 1;

  const onPause = () => {
    const promise = Promise.allSettled(
      pausableWorkspaces.map((workspace) =>
        pauseWorkSpaceMutation.mutateAsync({
          group_id: workspace.workgroup_id,
          space_id: workspace.workspace_id,
        })
      )
    ).then((results) => {
      const failureReasons: unknown[] = [];
      results.forEach((result) => {
        if (result.status === 'rejected') {
          failureReasons.push(result.reason);
        }
      });
      if (failureReasons.length > 0) {
        const firstReason = failureReasons[0];
        const reason =
          firstReason instanceof Error ? getErrorMessage(firstReason) : String(firstReason ?? 'Unknown error');
        throw new Error(
          failureReasons.length === pausableWorkspaces.length
            ? reason
            : `${failureReasons.length} of ${pausableWorkspaces.length} pause requests failed: ${reason}`
        );
      }
      return results.length;
    });

    toast.promise(promise, {
      loading: `Pausing ${pausableWorkspaces.length} ${
        pausableWorkspaces.length === 1 ? 'workspace' : 'workspaces'
      }...`,
      success: (count) => `Pause request accepted for ${count} ${count === 1 ? 'workspace' : 'workspaces'}.`,
      error: (err) => getErrorMessage(err),
    });

    onClearSelection();
  };

  const onTerminate = async () => {
    setShowTerminateConfirm(false);
    setTerminateAcknowledged(false);

    setDeletingSpaceIds((ids) => {
      const next = new Set(ids);
      for (const workspace of terminableWorkspaces) {
        next.add(workspace.workspace_id);
      }
      return next;
    });

    const promise = Promise.allSettled(
      terminableWorkspaces.map((workspace) =>
        deleteWorkSpaceMutation.mutateAsync({
          group_id: workspace.workgroup_id,
          space_id: workspace.workspace_id,
        })
      )
    ).then((results) => {
      const failed: { workspace: WorkspaceT; reason: unknown }[] = [];
      const succeeded: WorkspaceT[] = [];
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          failed.push({ workspace: terminableWorkspaces[index], reason: result.reason });
        } else {
          succeeded.push(terminableWorkspaces[index]);
        }
      });

      // Only poll workspaces whose delete request actually succeeded.
      if (succeeded.length > 0) {
        setWorkspacesPolling((polling) => {
          const next = [...polling];
          for (const workspace of succeeded) {
            if (!next.find((item) => item.workspace_id === workspace.workspace_id)) {
              next.push(workspace);
            }
          }
          return next;
        });
      }

      if (failed.length > 0) {
        // Restore deleting state for failed terminations.
        setDeletingSpaceIds((ids) => {
          const next = new Set(ids);
          for (const item of failed) {
            next.delete(item.workspace.workspace_id);
          }
          return next;
        });
        const firstFailureReason = failed[0].reason;
        const reason =
          firstFailureReason instanceof Error
            ? getErrorMessage(firstFailureReason)
            : String(firstFailureReason ?? 'Unknown error');
        throw new Error(
          failed.length === terminableWorkspaces.length
            ? reason
            : `${failed.length} of ${terminableWorkspaces.length} terminate requests failed: ${reason}`
        );
      }

      return results.length;
    });

    toast.promise(promise, {
      loading: `Terminating ${terminableWorkspaces.length} ${
        terminableWorkspaces.length === 1 ? 'workspace' : 'workspaces'
      }...`,
      success: (count) => `Terminate request accepted for ${count} ${count === 1 ? 'workspace' : 'workspaces'}.`,
      error: (err) => getErrorMessage(err),
    });

    onClearSelection();
  };

  const manageAccessTarget = canManageAccess ? adminWorkspaces[0] : null;

  return (
    <>
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '12px 16px',
          backgroundColor: theme.colors['background.informative.subtler'],
          borderRadius: '4px',
          flexWrap: 'wrap',
        })}
        role="region"
        aria-label="Selected workspaces actions"
      >
        <span
          className={css({
            fontSize: '14px',
            fontWeight: 500,
            color: theme.colors['text.primary'],
          })}
        >
          {selectedWorkspaces.length} {selectedWorkspaces.length === 1 ? 'workspace' : 'workspaces'} selected
        </span>
        <Button kind="secondary" size="compact" onClick={onClearSelection}>
          Clear Selection
        </Button>
        <div
          className={css({
            display: 'flex',
            gap: '8px',
            marginLeft: 'auto',
            flexWrap: 'wrap',
          })}
        >
          <Button kind="secondary" disabled={!canPause} onClick={onPause}>
            Pause
          </Button>
          <Button
            kind="secondary"
            disabled={!canTerminate}
            onClick={() => setShowTerminateConfirm(true)}
            overrides={{
              BaseButton: {
                style: {
                  color: canTerminate ? theme.colors['text.danger'] : undefined,
                  ...expand({
                    borderColor: canTerminate ? theme.colors['border.danger'] : undefined,
                  }),
                },
              },
            }}
          >
            Terminate
          </Button>
          <Button
            kind="secondary"
            disabled={!canManageAccess}
            onClick={() => {
              if (manageAccessTarget) {
                setShowAccessForWorkspace(manageAccessTarget);
              }
            }}
            aria-label={
              !canManageAccess
                ? 'Manage access supports a single workspace at a time. Select exactly one workspace where you have admin access.'
                : 'Manage access'
            }
          >
            Manage access
          </Button>
        </div>
      </div>
      {showTerminateConfirm ? (
        <Modal
          isOpen={showTerminateConfirm}
          onClose={() => {
            setShowTerminateConfirm(false);
            setTerminateAcknowledged(false);
          }}
        >
          <ModalHeader>Terminate {terminableWorkspaces.length === 1 ? 'workspace' : 'workspaces'}?</ModalHeader>
          <ModalBody>
            <div
              className={css({
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              })}
            >
              <p>
                Are you sure to terminate the following {terminableWorkspaces.length === 1 ? 'workspace' : 'workspaces'}
                ?
              </p>
              <ul
                className={css({
                  paddingLeft: '20px',
                  margin: 0,
                  listStyle: 'disc',
                  maxHeight: '180px',
                  overflowY: 'auto',
                })}
              >
                {terminableWorkspaces.map((workspace) => (
                  <li key={workspace.workspace_id}>
                    <strong>{workspace.name}</strong>
                    {workspace.is_rw ? ' (Read-Write)' : ' (Read-Only)'}
                  </li>
                ))}
              </ul>
              <p>
                This action will be performed for each workspace one by one. Associated database backups or snapshots
                will be permanently removed.
              </p>
              <Checkbox
                checked={terminateAcknowledged}
                onChange={(e) => setTerminateAcknowledged((e.target as HTMLInputElement).checked)}
              >
                <Label $style={{ marginTop: '1px' }}>
                  I understand that the workspaces and their associated database backups or snapshots will be deleted.
                </Label>
              </Checkbox>
            </div>
          </ModalBody>
          <ModalFooter>
            <ModalButton
              kind="secondary"
              onClick={() => {
                setShowTerminateConfirm(false);
                setTerminateAcknowledged(false);
              }}
            >
              Cancel
            </ModalButton>
            <ModalButton kind="destructive" disabled={!terminateAcknowledged} onClick={onTerminate}>
              Confirm
            </ModalButton>
          </ModalFooter>
        </Modal>
      ) : null}
      {showAccessForWorkspace ? (
        <SpaceUserManagement
          group={group}
          space={showAccessForWorkspace}
          isOpen={true}
          onClose={() => setShowAccessForWorkspace(null)}
        />
      ) : null}
    </>
  );
}
